import { useCallback, useState } from "react";
import { toast } from "sonner";
import { useUserData } from "store/user-data";

/** The store's public address. business_domain is normally the full host, but older logins may hold just the prefix. */
export function storeUrl(domain?: string | null) {
  const host = (domain || "").trim().replace(/^https?:\/\//, "").replace(/\/+$/, "");
  if (!host) return "";
  return `https://${host.includes(".") ? host : `${host}.storbolt.com`}`;
}

const copiedKey = (businessId?: string) => `storbolt:store-link-copied:${businessId || "unknown"}`;

function readCopied(businessId?: string) {
  try {
    return localStorage.getItem(copiedKey(businessId)) === "1";
  } catch {
    return false;
  }
}

/** Copies text, with a fallback for browsers that block the clipboard API. */
export async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    area.remove();
    return ok;
  }
}

/**
 * The store's link and a "Copy Link" action. Remembers (per store, in this browser) that the link
 * has been copied, which ticks off the dashboard's "Copy & share link" step.
 */
export function useStoreLink() {
  const { userData } = useUserData();
  const url = storeUrl(userData?.business_domain);
  const [copied, setCopied] = useState(() => readCopied(userData?.business_id));

  const copy = useCallback(async () => {
    if (!url) return;
    if (await copyText(url)) {
      toast.success("Store link copied. Share it with your customers!");
      try {
        localStorage.setItem(copiedKey(userData?.business_id), "1");
      } catch {
        // Not remembered in private windows; the copy itself still worked.
      }
      setCopied(true);
    } else {
      toast.error("Couldn't copy the link. Select it and copy it yourself.");
    }
  }, [url, userData?.business_id]);

  return { url, copied, copy };
}
